import { TheHero } from './components/hero';
import { TheMarketList } from './components/market-list';
import { TrendingNow } from './components/trending-now';
import { TrustBand } from './components/trust-band';
import { useHomeStore } from './store/home-store';
import { FEEDBACK_LINK, isRealLink } from '@/client/confs/app';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Lazy: sections pull in the chart stack (lightweight-charts); they already
// mount deferred (idle callback below), so code-splitting them keeps charts
// out of the home page's initial JS without changing what the user sees.
const TheMarketSections = lazy(() => import('./components/market-sections').then(m => ({ default: m.TheMarketSections })));

const useDeferredMount = (delayMs = 1500) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setReady(true), { timeout: delayMs });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(id);
  }, [delayMs]);
  return ready;
};

const TheHomePage = () => {
  const { t } = useTranslation(['home']);
  const category = useHomeStore(state => state.category);
  const sectionsReady = useDeferredMount();

  return (
    <section className="w-full pb-18 md:pb-14 min-h-full">
      <div className="max-w-[1400px] mx-auto">
        {/* hero replaces the promo banner carousel (client-confirmed) */}
        <TheHero />
        {/* Design handoff: Trending now section above the browse list */}
        <TrendingNow />
        {/* SakerX beta: no native-token narrative (single-collateral USDT, no
            staking). The AIP utility band + TokenTeaser it superseded were
            removed; the compliance-first trust band (honest copy, no metrics)
            takes the secondary "why trust this" slot in the Split Studio rhythm. */}
        <TrustBand />
        <div id="market-list">
          {/* Design handoff: Browse markets section header */}
          <div className="neon-sechead px-4 md:px-6 pt-6">
            <div>
              <h2>{t('browse-markets', 'Browse markets')}</h2>
              <p>{t('browse-markets-sub', 'Open markets across every category.')}</p>
            </div>
          </div>
          <TheMarketList />
        </div>
        {/* fallback null: the component renders its own skeletons while data
            loads, so a nearer boundary avoids re-triggering the app-level
            BlockLoading suspense over the whole page */}
        {category === 'trending' && sectionsReady && (
          <Suspense fallback={null}>
            <TheMarketSections />
          </Suspense>
        )}
        {/* The question only makes sense with its feedback link; standalone it
            reads as a dangling fragment (taste audit F3). */}
        {isRealLink(FEEDBACK_LINK) && (
          <p className="text-center text-sm text-muted-foreground py-12 px-4">
            {t('no-predictions-of-interest')}{' '}
            <a
              href={FEEDBACK_LINK}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-primary transition-colors"
            >
              {t('send-us-feedback')}
            </a>
          </p>
        )}
      </div>
    </section>
  );
};

export default TheHomePage;
